package com.checkin.host.api

import com.checkin.shared.dto.CheckoutResponse
import com.checkin.shared.dto.CreateCheckoutRequest
import com.checkin.shared.dto.SubscriptionDto
import com.checkin.shared.dto.ApiError
import com.checkin.shared.dto.AuthResponse
import com.checkin.shared.dto.CreatePropertyRequest
import com.checkin.shared.dto.GuestDto
import com.checkin.shared.dto.GuestPersonalDataExportDto
import com.checkin.shared.dto.PurgePersonalDataResponse
import com.checkin.shared.dto.CreateReservationRequest
import com.checkin.shared.dto.CalendarMonthDto
import com.checkin.shared.dto.ContractTemplateDto
import com.checkin.shared.dto.DashboardStatsDto
import com.checkin.shared.dto.SaveContractTemplateRequest
import com.checkin.shared.dto.UpdateContractTemplateRequest
import com.checkin.host.util.apiBaseUrl
import com.checkin.host.util.toBase64
import com.checkin.shared.dto.ForgotPasswordRequest
import com.checkin.shared.dto.GoogleAuthRequest
import com.checkin.shared.dto.LoginRequest
import com.checkin.shared.dto.PropertyDto
import com.checkin.shared.dto.RegisterRequest
import com.checkin.shared.dto.MessageResponse
import com.checkin.shared.dto.PublicAppConfigDto
import com.checkin.shared.dto.RegisterResponse
import com.checkin.shared.dto.ResendVerificationRequest
import com.checkin.shared.dto.ResetPasswordRequest
import com.checkin.shared.dto.ReservationDto
import com.checkin.shared.dto.ReservationVerificationMediaDto
import com.checkin.shared.dto.UpdateGuestContractFieldsRequest
import com.checkin.shared.dto.UpdateProfileRequest
import com.checkin.shared.dto.UpdatePropertyRequest
import com.checkin.shared.dto.UpdateReservationRequest
import com.checkin.shared.dto.UserDto
import com.checkin.shared.dto.VerificationLinkResponse
import com.checkin.shared.model.PropertyType
import com.checkin.shared.model.ReservationStatus
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.request.url
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import kotlinx.browser.localStorage

class ApiException(val status: Int, override val message: String) : Exception(message)

private inline fun <reified T> HttpRequestBuilder.setJsonBody(body: T) {
    contentType(ContentType.Application.Json)
    setBody(body)
}

object ApiClient {
    private fun baseUrl(): String = apiBaseUrl()
    private const val TOKEN_KEY = "checkin_token"
    private const val USER_KEY = "checkin_user"

    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
        encodeDefaults = true
    }

    val http = HttpClient {
        install(ContentNegotiation) { json(json) }
    }

    var token: String?
        get() = localStorage.getItem(TOKEN_KEY)
        set(value) {
            if (value == null) localStorage.removeItem(TOKEN_KEY)
            else localStorage.setItem(TOKEN_KEY, value)
        }

    fun saveUser(user: UserDto) {
        localStorage.setItem(USER_KEY, json.encodeToString(UserDto.serializer(), user))
    }

    fun loadUser(): UserDto? =
        localStorage.getItem(USER_KEY)?.let { json.decodeFromString(UserDto.serializer(), it) }

    fun clearSession() {
        token = null
        localStorage.removeItem(USER_KEY)
    }

    fun isLoggedIn(): Boolean = !token.isNullOrBlank()

    private suspend inline fun <reified T> authorized(block: suspend () -> HttpResponse): T {
        val response = block()
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            val err = runCatching { response.body<ApiError>() }.getOrNull()
            throw ApiException(response.status.value, err?.error ?: response.bodyAsText())
        }
        return response.body()
    }

    suspend fun fetchPublicAppConfig(): PublicAppConfigDto {
        val response = http.get("${baseUrl()}/public/app-config")
        if (!response.status.isSuccess()) {
            return PublicAppConfigDto(registrationEnabled = true)
        }
        return response.body()
    }

    suspend fun register(request: RegisterRequest): RegisterResponse {
        val response = http.post("${baseUrl()}/auth/register") { setJsonBody(request) }
        if (!response.status.isSuccess()) {
            val err = runCatching { response.body<ApiError>() }.getOrNull()
            throw ApiException(response.status.value, err?.error ?: "Erreur inscription")
        }
        return response.body()
    }

    suspend fun login(request: LoginRequest): AuthResponse {
        val response = http.post("${baseUrl()}/auth/login") { setJsonBody(request) }
        if (!response.status.isSuccess()) {
            val err = runCatching { response.body<ApiError>() }.getOrNull()
            throw ApiException(response.status.value, err?.error ?: "Identifiants invalides")
        }
        return response.body()
    }

    suspend fun loginWithGoogle(request: GoogleAuthRequest): AuthResponse {
        val response = http.post("${baseUrl()}/auth/google") { setJsonBody(request) }
        if (!response.status.isSuccess()) {
            val err = runCatching { response.body<ApiError>() }.getOrNull()
            throw ApiException(response.status.value, err?.error ?: "Connexion Google impossible")
        }
        return response.body()
    }

    suspend fun verifyEmail(token: String): AuthResponse {
        val response = http.get("${baseUrl()}/auth/verify-email") {
            url { parameters.append("token", token) }
        }
        if (!response.status.isSuccess()) {
            val err = runCatching { response.body<ApiError>() }.getOrNull()
            throw ApiException(response.status.value, err?.error ?: "Lien invalide")
        }
        return response.body()
    }

    suspend fun resendVerification(email: String): MessageResponse {
        val response = http.post("${baseUrl()}/auth/resend-verification") {
            setJsonBody(ResendVerificationRequest(email))
        }
        if (!response.status.isSuccess()) {
            val err = runCatching { response.body<ApiError>() }.getOrNull()
            throw ApiException(response.status.value, err?.error ?: "Erreur")
        }
        return response.body()
    }

    suspend fun forgotPassword(email: String): MessageResponse {
        val response = http.post("${baseUrl()}/auth/forgot-password") {
            setJsonBody(ForgotPasswordRequest(email))
        }
        if (!response.status.isSuccess()) {
            val err = runCatching { response.body<ApiError>() }.getOrNull()
            throw ApiException(response.status.value, err?.error ?: "Erreur")
        }
        return response.body()
    }

    suspend fun resetPassword(token: String, newPassword: String): MessageResponse {
        val response = http.post("${baseUrl()}/auth/reset-password") {
            setJsonBody(ResetPasswordRequest(token, newPassword))
        }
        if (!response.status.isSuccess()) {
            val err = runCatching { response.body<ApiError>() }.getOrNull()
            throw ApiException(response.status.value, err?.error ?: "Erreur")
        }
        return response.body()
    }

    suspend fun me(): UserDto = authorized {
        http.get("${baseUrl()}/me") { header("Authorization", "Bearer $token") }
    }

    suspend fun updateProfile(request: UpdateProfileRequest): UserDto = authorized {
        http.put("${baseUrl()}/me") {
            header("Authorization", "Bearer $token")
            setJsonBody(request)
        }
    }

    suspend fun hostSignature(): ByteArray? {
        val response = http.get("${baseUrl()}/me/host-signature") {
            header("Authorization", "Bearer $token")
        }
        if (response.status == HttpStatusCode.NotFound) return null
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            val text = runCatching { response.bodyAsText() }.getOrDefault("")
            val msg = runCatching { json.decodeFromString<ApiError>(text).error }.getOrNull()
            throw ApiException(response.status.value, msg ?: "Erreur serveur")
        }
        return response.body()
    }

    suspend fun hostLogo(): ByteArray? = hostBrandAsset("/me/host-logo")

    suspend fun hostStamp(): ByteArray? = hostBrandAsset("/me/host-stamp")

    private suspend fun hostBrandAsset(path: String): ByteArray? {
        val response = http.get("${baseUrl()}$path") {
            header("Authorization", "Bearer $token")
        }
        if (response.status == HttpStatusCode.NotFound) return null
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            val text = runCatching { response.bodyAsText() }.getOrDefault("")
            val msg = runCatching { json.decodeFromString<ApiError>(text).error }.getOrNull()
            throw ApiException(response.status.value, msg ?: "Erreur serveur")
        }
        return response.body()
    }

    suspend fun listProperties(city: String? = null, type: PropertyType? = null): List<PropertyDto> =
        authorized {
            val params = buildList {
                city?.takeIf { it.isNotBlank() }?.let { add("city=$it") }
                type?.let { add("type=${it.name}") }
            }
            val query = if (params.isEmpty()) "" else "?${params.joinToString("&")}"
            http.get("${baseUrl()}/properties$query") { header("Authorization", "Bearer $token") }
        }

    suspend fun createProperty(request: CreatePropertyRequest): PropertyDto = authorized {
        http.post("${baseUrl()}/properties") {
            header("Authorization", "Bearer $token")
            setJsonBody(request)
        }
    }

    suspend fun updateProperty(id: String, request: UpdatePropertyRequest): PropertyDto = authorized {
        http.put("${baseUrl()}/properties/$id") {
            header("Authorization", "Bearer $token")
            setJsonBody(request)
        }
    }

    suspend fun propertyPhoto(propertyId: String): ByteArray = authorized {
        val response = http.get("${baseUrl()}/properties/$propertyId/photo") {
            header("Authorization", "Bearer $token")
        }
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            val text = runCatching { response.bodyAsText() }.getOrDefault("")
            val msg = runCatching { json.decodeFromString<ApiError>(text).error }.getOrNull()
                ?: text.ifBlank { "Photo indisponible" }
            throw ApiException(response.status.value, msg)
        }
        response.body()
    }

    suspend fun archiveProperty(id: String): PropertyDto = authorized {
        http.post("${baseUrl()}/properties/$id/archive") { header("Authorization", "Bearer $token") }
    }

    suspend fun reactivateProperty(id: String): PropertyDto = authorized {
        http.post("${baseUrl()}/properties/$id/reactivate") { header("Authorization", "Bearer $token") }
    }

    suspend fun deleteProperty(id: String) {
        val response = http.delete("${baseUrl()}/properties/$id") { header("Authorization", "Bearer $token") }
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            throw ApiException(response.status.value, "Suppression impossible")
        }
    }

    suspend fun listReservations(
        propertyId: String? = null,
        status: ReservationStatus? = null,
        checkInFrom: String? = null,
        checkOutTo: String? = null,
    ): List<ReservationDto> = authorized {
        val params = buildList {
            propertyId?.let { add("propertyId=$it") }
            status?.let { add("status=${it.name}") }
            checkInFrom?.let { add("checkInFrom=$it") }
            checkOutTo?.let { add("checkOutTo=$it") }
        }
        val query = if (params.isEmpty()) "" else "?${params.joinToString("&")}"
        http.get("${baseUrl()}/reservations$query") { header("Authorization", "Bearer $token") }
    }

    suspend fun createReservation(request: CreateReservationRequest): ReservationDto = authorized {
        http.post("${baseUrl()}/reservations") {
            header("Authorization", "Bearer $token")
            setJsonBody(request)
        }
    }

    suspend fun updateReservation(id: String, request: UpdateReservationRequest): ReservationDto = authorized {
        http.put("${baseUrl()}/reservations/$id") {
            header("Authorization", "Bearer $token")
            setJsonBody(request)
        }
    }

    suspend fun deleteReservation(id: String) {
        val response = http.delete("${baseUrl()}/reservations/$id") { header("Authorization", "Bearer $token") }
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            throw ApiException(response.status.value, "Suppression impossible")
        }
    }

    suspend fun verificationLink(reservationId: String): VerificationLinkResponse = authorized {
        http.get("${baseUrl()}/reservations/$reservationId/verification-link") {
            header("Authorization", "Bearer $token")
        }
    }

    suspend fun sendInvitationEmail(reservationId: String): MessageResponse = authorized {
        http.post("${baseUrl()}/reservations/$reservationId/send-invitation-email") {
            header("Authorization", "Bearer $token")
        }
    }

    suspend fun dashboardStats(): DashboardStatsDto = authorized {
        http.get("${baseUrl()}/dashboard/stats") { header("Authorization", "Bearer $token") }
    }

    suspend fun dashboardCalendar(month: String): CalendarMonthDto = authorized {
        http.get("${baseUrl()}/dashboard/calendar?month=$month") { header("Authorization", "Bearer $token") }
    }

    suspend fun listContractTemplates(): List<ContractTemplateDto> = authorized {
        http.get("${baseUrl()}/contract-templates") { header("Authorization", "Bearer $token") }
    }

    suspend fun deleteContractTemplate(id: String) {
        val response = http.delete("${baseUrl()}/contract-templates/$id") { header("Authorization", "Bearer $token") }
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            val text = runCatching { response.bodyAsText() }.getOrDefault("")
            val msg = runCatching { json.decodeFromString<ApiError>(text).error }.getOrNull()
            throw ApiException(response.status.value, msg ?: "Suppression impossible")
        }
    }

    suspend fun createContractTemplate(name: String, bodyText: String): ContractTemplateDto =
        authorized {
            http.post("${baseUrl()}/contract-templates") {
                header("Authorization", "Bearer $token")
                setJsonBody(SaveContractTemplateRequest(name = name, bodyText = bodyText))
            }
        }

    suspend fun updateContractTemplate(
        id: String,
        name: String? = null,
        bodyText: String? = null,
    ): ContractTemplateDto = authorized {
        http.put("${baseUrl()}/contract-templates/$id") {
            header("Authorization", "Bearer $token")
            setJsonBody(UpdateContractTemplateRequest(name = name, bodyText = bodyText))
        }
    }

    fun contractPreviewUrl(templateId: String, reservationId: String? = null): String {
        val base = "${baseUrl()}/contract-templates/$templateId/preview"
        return if (reservationId != null) "$base?reservationId=$reservationId" else base
    }

    suspend fun downloadContractPreview(templateId: String, reservationId: String? = null): ByteArray {
        val url = contractPreviewUrl(templateId, reservationId)
        val response = http.get(url) { header("Authorization", "Bearer $token") }
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            val text = runCatching { response.bodyAsText() }.getOrDefault("")
            val message = runCatching { json.decodeFromString<ApiError>(text).error }.getOrNull()
                ?: text.ifBlank { "Aperçu indisponible" }
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    suspend fun approveManualVerification(reservationId: String, guestId: String): GuestDto = authorized {
        http.post("${baseUrl()}/reservations/$reservationId/guests/$guestId/approve-manual-verification") {
            header("Authorization", "Bearer $token")
        }
    }

    suspend fun approveHostReview(reservationId: String, guestId: String): GuestDto = authorized {
        http.post("${baseUrl()}/reservations/$reservationId/guests/$guestId/approve-host-review") {
            header("Authorization", "Bearer $token")
        }
    }

    suspend fun requestContractResign(
        reservationId: String,
        guestId: String,
        request: UpdateGuestContractFieldsRequest,
    ): VerificationLinkResponse = authorized {
        http.post("${baseUrl()}/reservations/$reservationId/guests/$guestId/request-contract-resign") {
            header("Authorization", "Bearer $token")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
    }

    suspend fun signedContractPdf(reservationId: String, guestId: String): ByteArray = authorized {
        val response = http.get("${baseUrl()}/reservations/$reservationId/guests/$guestId/signed-contract") {
            header("Authorization", "Bearer $token")
        }
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            val text = runCatching { response.bodyAsText() }.getOrDefault("")
            val msg = runCatching { json.decodeFromString<ApiError>(text).error }.getOrNull()
                ?: text.ifBlank { "Contrat indisponible" }
            throw ApiException(response.status.value, msg)
        }
        response.body()
    }

    suspend fun listVerificationMedia(reservationId: String): ReservationVerificationMediaDto = authorized {
        http.get("${baseUrl()}/reservations/$reservationId/verification-media") {
            header("Authorization", "Bearer $token")
        }
    }

    suspend fun getSubscription(): SubscriptionDto = authorized {
        http.get("${baseUrl()}/billing/subscription") { header("Authorization", "Bearer $token") }
    }

    suspend fun createCheckout(plan: String): CheckoutResponse = authorized {
        http.post("${baseUrl()}/billing/checkout") {
            header("Authorization", "Bearer $token")
            setJsonBody(CreateCheckoutRequest(plan = plan))
        }
    }

    suspend fun getCustomerPortalUrl(): CheckoutResponse = authorized {
        http.get("${baseUrl()}/billing/customer-portal") { header("Authorization", "Bearer $token") }
    }

    suspend fun verificationMediaImage(
        reservationId: String,
        guestId: String,
        storageKey: String,
    ): ByteArray = authorized {
        val key = encodeUriComponent(storageKey)
        val response = http.get(
            "${baseUrl()}/reservations/$reservationId/guests/$guestId/verification-media/file?key=$key",
        ) { header("Authorization", "Bearer $token") }
        if (response.status == HttpStatusCode.Unauthorized) {
            clearSession()
            throw ApiException(401, "Session expirée")
        }
        if (!response.status.isSuccess()) {
            val text = runCatching { response.bodyAsText() }.getOrDefault("")
            val msg = runCatching { json.decodeFromString<ApiError>(text).error }.getOrNull()
                ?: text.ifBlank { "Image indisponible" }
            throw ApiException(response.status.value, msg)
        }
        response.body()
    }

    suspend fun exportGuestPersonalData(
        reservationId: String,
        guestId: String,
    ): GuestPersonalDataExportDto = authorized {
        http.get("${baseUrl()}/reservations/$reservationId/guests/$guestId/personal-data-export") {
            header("Authorization", "Bearer $token")
        }
    }

    suspend fun purgeGuestPersonalData(
        reservationId: String,
        guestId: String,
    ): PurgePersonalDataResponse = authorized {
        http.delete("${baseUrl()}/reservations/$reservationId/guests/$guestId/personal-data") {
            header("Authorization", "Bearer $token")
        }
    }
}

@Suppress("UnsafeCastFromDynamic")
private fun encodeUriComponent(value: String): String = js("encodeURIComponent")(value) as String
